import axios from 'axios';
import { textoDaValidacao, textoPeloStatus } from './textoDoErro';

// Usar URL relativa para evitar problemas de CORS
const BACKEND_URL = '';

// Criar instância do axios com configurações otimizadas
const api = axios.create({
  baseURL: '/api',  // URL relativa
  timeout: 30000, // 30 segundos de timeout
  withCredentials: true, // envia o cookie HttpOnly de sessão (mesma origem)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // ══════════════════════════════════════════════════════════════════════════════════════
    // ENVIO DE ARQUIVO: O `Content-Type` PADRÃO ACIMA COMIA O ANEXO — silenciosamente
    // ══════════════════════════════════════════════════════════════════════════════════════
    // Dono, 2026-09-08: *"estou tentando inserir a planilha via obras e está apresentando erro
    // de validação"*. A planilha estava certa (medida: 398 linhas, o importador a processa
    // inteira). O arquivo nunca chegava ao servidor.
    //
    // A instância acima declara `headers: { 'Content-Type': 'application/json' }`, e o axios 1.x
    // faz isto em `transformRequest` (`node_modules/axios/dist/node/axios.cjs`, conferido na
    // versão instalada):
    //
    //     const isFormData = utils.isFormData(data);
    //     if (isFormData) { return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data; }
    //
    // Ou seja: com `application/json` declarado, ele CONVERTE o FormData em JSON. O `File` vira
    // `{}` e o corpo sai como `{"arquivo":{}}`. O FastAPI, que espera `multipart/form-data` para
    // `UploadFile = File(...)`, responde 422 — o "erro de validação" da tela.
    //
    // O adapter até chama `setContentType(undefined)` para FormData (linha ~4802 do mesmo
    // arquivo), mas isso roda DEPOIS: quando ele executa, o FormData já virou string.
    //
    // AQUI, E NÃO EM CADA TELA (regra 5 do CLAUDE.md): as TRÊS telas que enviam FormData
    // (planilha de obras, cadastros incompletos e o anexo do chat) — e a próxima. Apagando o
    // cabeçalho, o navegador escreve `multipart/form-data; boundary=...` — que é o único jeito
    // de o boundary existir, porque só ele conhece o valor.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      if (config.headers.common) delete config.headers.common['Content-Type'];
    }
    // Inicializar contador de retry
    config._retryCount = config._retryCount || 0;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Função de delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Interceptor para tratar erros e retry automático
/**
 * AVISOS DO SERVIDOR QUE NINGUÉM LIA.
 *
 * `GET /demandas` responde `X-Resultado-Truncado` quando a janela cortou, e
 * `X-Documentos-Ilegiveis` quando pulou documento que não passou na validação. Os dois nasceram
 * para o corte deixar de ser silencioso — mas ficaram sendo o MESMO silêncio, porque nenhuma
 * tela os lia (medido: zero ocorrências em `frontend/src`). Um cabeçalho que ninguém lê é o
 * `logger.warning` que a regra 6 do CLAUDE.md chama de silêncio.
 *
 * Aqui, no interceptor, o aviso alcança TODA tela que consome a rota — Programação, Mapa, o
 * "adicionar a uma OS existente" — sem cada uma precisar lembrar de conferir.
 */
api.interceptors.response.use(
  (response) => {
    const h = response?.headers || {};
    if (h['x-resultado-truncado'] === 'true') {
      // eslint-disable-next-line no-console
      console.warn('[api] lista TRUNCADA em', h['x-resultado-limite'], 'itens:', response.config?.url);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('api:resultado-truncado', {
          detail: { limite: h['x-resultado-limite'], url: response.config?.url } }));
      }
    }
    if (h['x-documentos-ilegiveis']) {
      // eslint-disable-next-line no-console
      console.error('[api]', h['x-documentos-ilegiveis'],
                    'documento(s) ILEGÍVEL(is) foram pulados em', response.config?.url);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('api:documentos-ilegiveis', {
          detail: { n: h['x-documentos-ilegiveis'], url: response.config?.url } }));
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const maxRetries = 3;
    
    // Métodos idempotentes podem ser re-tentados com segurança. POST/PATCH NÃO são
    // idempotentes — re-enviar em timeout/erro de rede pode criar registro duplicado
    // (ex.: 2 RDOs, 2 solicitações). Só re-tentamos GET/HEAD/PUT/DELETE.
    const metodo = (originalRequest?.method || 'get').toLowerCase();
    const metodoIdempotente = ['get', 'head', 'options', 'put', 'delete'].includes(metodo);
    // O PEDIDO QUE NÃO É IDEMPOTENTE MESMO SENDO PUT diz `{ semRetentativa: true }` na config. O PUT da
    // solicitação de material leva a VERSÃO que a tela leu: o reenvio de um PUT que já gravou (a resposta se
    // perdeu no 4G) levava 409 e a tela dizia "nada foi gravado" — gravado (cético das telas, 2026-09-15).
    const semRetentativa = !!originalRequest?.semRetentativa;

    // Verificar se deve fazer retry
    const shouldRetry = metodoIdempotente && !semRetentativa && (
      // Erro de rede
      error.message === 'Network Error' ||
      error.code === 'ECONNABORTED' ||
      // Erro 5xx do servidor (incluindo 520)
      (error.response && error.response.status >= 500 && error.response.status < 600) ||
      // Sem resposta
      !error.response
    );

    if (shouldRetry && originalRequest._retryCount < maxRetries) {
      originalRequest._retryCount += 1;
      
      // Delay exponencial: 1s, 2s, 4s
      const delayMs = Math.pow(2, originalRequest._retryCount - 1) * 1000;
      console.log(`[API] Retry ${originalRequest._retryCount}/${maxRetries} em ${delayMs/1000}s - ${originalRequest.url}`);
      
      await delay(delayMs);
      return api(originalRequest);
    }
    
    // 403 com troca de senha obrigatória (setada, ex.: por um admin, numa sessão já ativa):
    // marca a flag no usuário local e recarrega, para o App exibir a tela de troca obrigatória
    // (o backend já bloqueia todas as demais rotas). Evita o estado "zumbi" nesse caso.
    const detail403 = error.response?.status === 403 ? error.response?.data?.detail : null;
    const codeMustChange = detail403 && typeof detail403 === 'object' && detail403.code === 'must_change_password';
    if (codeMustChange) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || 'null');
        if (u && !u.must_change_password) {
          localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: true }));
          window.location.reload();
        }
      } catch (_) { /* ignore */ }
    }

    // Se for erro 401 (não autorizado), limpar sessão E redirecionar ao login — sem isto
    // o app fica "zumbi" quando a sessão expira com a aba aberta (todas as chamadas falham
    // mas a UI logada continua na tela). Evita loop na própria tela de login.
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const naTelaDeLogin = /\/(login|redefinir-senha|recuperar-senha)/.test(window.location.pathname);
      if (!naTelaDeLogin) {
        // pequena espera para o interceptor resolver antes de navegar
        window.location.assign('/login');
      }
    }

    // UMA MENSAGEM QUE SEMPRE É TEXTO. `detail` chega em três formas — string, dicionário
    // (`{msg, impedimentos, pendencias, ...}` das rotas com portão) e LISTA (o 422 do pydantic).
    // Jogado cru num `toast.error`, o dicionário vira "[object Object]" e a lista pode derrubar
    // a tela inteira: quem está em campo lê isso como "o sistema recusou o meu RDO".
    //
    // Normalizar aqui, e não em cada `catch`, é o que fecha a classe: são dezenas de telas, e
    // basta uma esquecida para o defeito voltar. `error.detalhe` fica ao lado para quem
    // precisar do objeto (mostrar a lista de pendências, navegar para o registro em conflito).
    const _d = error.response?.data?.detail;
    error.detalhe = _d;
    error.mensagem = (
      typeof _d === 'string' ? _d
        : _d?.msg || _d?.mensagem
        || (Array.isArray(_d?.pendencias) && _d.pendencias.length
          ? `${_d.pendencias.length} item(ns) pendente(s)` : null)
        || (Array.isArray(_d) ? textoDaValidacao(_d) : null)
        || null
    );
    // E O `message` DO AXIOS DEIXA DE SER INGLÊS ("Network Error", "Request failed with status
    // code 500"): dezenas de `catch` mostram `e.message` direto. Pedido cancelado fica como está —
    // ninguém o mostra, e os `catch` o reconhecem pelo `code`/`name`.
    if (error.code !== 'ERR_CANCELED') {
      error.mensagemTecnica = error.message;
      error.message = error.mensagem || textoPeloStatus(error);
    }

    return Promise.reject(error);
  }
);

export default api;
export { BACKEND_URL };
